import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDateTime } from "../lib/date";
import RecordDeleteDialog from "../components/RecordDeleteDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Trash, Broom, Warning, MagnifyingGlass, ArrowLeft, ShieldStar, Plus, LockKey } from "@phosphor-icons/react";

function SuperAdminCard() {
  const [supers, setSupers] = useState([]);
  const [people, setPeople] = useState([]);
  const [pick, setPick] = useState("");
  const load = useCallback(() => {
    api.get("/admin/super-admins").then((r) => setSupers(r.data)).catch(() => {});
    api.get("/admin/grantable-people").then((r) => setPeople(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const grant = async () => {
    if (!pick) return;
    try { await api.post(`/admin/super-admins/${pick}`); toast.success("Super Admin granted"); setPick(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const revoke = async (id) => {
    try { await api.delete(`/admin/super-admins/${id}`); toast.success("Super Admin revoked"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const superIds = new Set(supers.map((s) => s.id));
  return (
    <div data-testid="super-admin-card" className="rounded-lg border-2 border-indigo-200 bg-indigo-50/60 p-4">
      <div className="font-display font-black uppercase text-sm text-indigo-800 flex items-center gap-2 mb-2"><ShieldStar size={18} weight="bold" />Super Admins</div>
      <p className="text-sm text-indigo-700/80 mb-3">Super Admins can permanently delete any record, including historical &amp; compliance data. Keep this list very small.</p>
      <div className="flex flex-col gap-1.5 mb-3">
        {supers.map((s) => (
          <div key={s.id} data-testid={`super-admin-${s.id}`} className="flex items-center justify-between bg-white rounded px-3 py-1.5 border border-indigo-100">
            <span className="text-sm font-semibold text-slate-800">{s.name} <span className="text-slate-400 font-normal">{s.email}</span></span>
            <button data-testid={`revoke-super-${s.id}`} onClick={() => revoke(s.id)} className="text-xs font-bold text-red-600 hover:underline">Revoke</button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <select data-testid="grant-super-select" value={pick} onChange={(e) => setPick(e.target.value)} className="flex-1 h-10 border-2 border-slate-300 rounded px-2 text-sm">
          <option value="">Grant Super Admin to…</option>
          {people.filter((p) => !superIds.has(p.id)).map((p) => <option key={p.id} value={p.id}>{`${p.name} — ${p.email}`}</option>)}
        </select>
        <Button data-testid="grant-super-btn" onClick={grant} disabled={!pick} className="h-10 bg-indigo-600 hover:bg-indigo-700 font-bold"><Plus size={15} weight="bold" className="mr-1" />Grant</Button>
      </div>
    </div>
  );
}

function BulkDeleteDialog({ entity, ids, protectedRec, isSuperAdmin, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const blocked = protectedRec && !isSuperAdmin;
  const ok = !protectedRec || (reason.trim() && txt.trim().toUpperCase() === "DELETE");
  const run = async () => {
    setBusy(true);
    try { const r = await api.post(`/admin/records/${entity}/bulk-delete`, { ids, reason: reason.trim() });
      toast.success(`Deleted ${r.data.deleted}${r.data.failed ? `, ${r.data.failed} failed` : ""}${r.data.skipped ? `, ${r.data.skipped} skipped` : ""}`); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="bulk-delete-dialog" className="bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700"><Warning size={20} weight="bold" />Delete {ids.length} records?</DialogTitle>
          <DialogDescription>These records will be permanently deleted. This cannot be undone.</DialogDescription>
        </DialogHeader>
        {blocked ? (
          <div className="text-sm font-bold text-red-700 bg-red-50 border border-red-200 rounded p-2">Super Admin permission is required to permanently delete these records.</div>
        ) : protectedRec && (
          <div className="flex flex-col gap-2">
            <Textarea data-testid="bulk-delete-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" />
            <Input data-testid="bulk-delete-confirm" value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="Type DELETE to confirm" className="h-9" />
          </div>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {!blocked && <Button data-testid="bulk-delete-confirm-btn" disabled={busy || !ok} onClick={run} className="bg-red-600 hover:bg-red-700 font-bold">Delete permanently</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDataManagerPage() {
  const { hasPerm, isSuperAdmin } = useAuth();
  const nav = useNavigate();
  const [sections, setSections] = useState([]);
  const [active, setActive] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [testOnly, setTestOnly] = useState(false);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null); // {id, label}
  const [bulkOpen, setBulkOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeTxt, setPurgeTxt] = useState("");

  const isAdmin = hasPerm("administrator");

  const loadSections = useCallback(() => {
    api.get("/admin/deletable").then((r) => { setSections(r.data); if (!active && r.data[0]) setActive(r.data[0].key); }).catch(() => {});
  }, [active]);
  const loadRows = useCallback(() => {
    if (!active) return;
    api.get(`/admin/records/${active}`, { params: { q, test_only: testOnly } }).then((r) => { setRows(r.data); setSel({}); }).catch(() => setRows([]));
  }, [active, q, testOnly]);

  useEffect(() => { if (isAdmin) loadSections(); }, [isAdmin, loadSections]);
  useEffect(() => { if (isAdmin) loadRows(); }, [isAdmin, loadRows]);

  if (!isAdmin) return <div className="p-6 text-slate-600" data-testid="admin-data-denied">Administrator access only.</div>;

  const activeSection = sections.find((s) => s.key === active);
  const activeLabel = activeSection?.label || "records";
  const activeProtected = !!activeSection?.protected;
  const selectedIds = Object.keys(sel).filter((k) => sel[k]);

  const refresh = () => { setDelTarget(null); setBulkOpen(false); loadRows(); loadSections(); };
  const doPurge = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/purge-test-data"); toast.success(`Purged ${r.data.total} test record(s)`); setPurgeOpen(false); setPurgeTxt(""); loadRows(); loadSections(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-5 max-w-5xl p-4" data-testid="admin-data-manager">
      <div className="flex items-center gap-3">
        <button onClick={() => nav("/admin")} data-testid="back-to-admin" className="text-slate-500 hover:text-slate-800"><ArrowLeft size={20} weight="bold" /></button>
        <div>
          <h1 className="font-display text-2xl font-black uppercase tracking-wide">Data &amp; Cleanup</h1>
          <p className="text-sm text-slate-500">Archive, delete or purge records. Protected historical/compliance data needs Super Admin.</p>
        </div>
      </div>

      {isSuperAdmin && <SuperAdminCard />}

      <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="purge-card">
        <div>
          <div className="font-display font-black uppercase text-sm text-red-800 flex items-center gap-2"><Broom size={18} weight="bold" />Purge test data</div>
          <p className="text-sm text-red-700/80">Removes every record whose name/number/title starts with “TEST”.</p>
        </div>
        <Button data-testid="purge-test-data-btn" disabled={busy} onClick={() => setPurgeOpen(true)} className="bg-red-600 hover:bg-red-700 font-bold whitespace-nowrap">Purge all TEST data</Button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="section-tabs">
        {sections.map((s) => (
          <button key={s.key} data-testid={`section-tab-${s.key}`} onClick={() => { setActive(s.key); setQ(""); }}
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide border-2 transition-colors flex items-center gap-1 ${active === s.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
            {s.protected && <LockKey size={12} weight="bold" className="opacity-70" />}{s.label} <span className="opacity-60">({s.count})</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="record-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${activeLabel}…`} className="pl-9 h-10" />
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <input type="checkbox" data-testid="test-only-toggle" checked={testOnly} onChange={(e) => setTestOnly(e.target.checked)} /> TEST only
        </label>
        {selectedIds.length > 0 && (
          <Button data-testid="bulk-delete-btn" disabled={busy} onClick={() => setBulkOpen(true)} className="bg-red-600 hover:bg-red-700 font-bold h-10"><Trash size={15} weight="bold" className="mr-1" />Delete {selectedIds.length}</Button>
        )}
      </div>

      {activeProtected && (
        <div className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 flex items-center gap-2" data-testid="protected-notice">
          <LockKey size={13} weight="bold" /> Protected section — permanent deletion requires Super Admin{isSuperAdmin ? " (you have it)" : " (you can archive instead)"}.
        </div>
      )}

      <div className="rounded-lg border-2 border-slate-200 overflow-hidden" data-testid="records-list">
        {rows.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">No records.</div>
        ) : rows.map((r) => (
          <div key={r.id} data-testid={`record-row-${r.id}`} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50">
            <input type="checkbox" data-testid={`record-check-${r.id}`} checked={!!sel[r.id]} onChange={(e) => setSel((p) => ({ ...p, [r.id]: e.target.checked }))} />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-800 truncate flex items-center gap-2">
                {r.label}
                {r.is_test && <span className="text-[10px] font-black uppercase bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">TEST</span>}
                {r.archived && <span className="text-[10px] font-black uppercase bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Archived</span>}
              </div>
              <div className="text-xs text-slate-400 font-mono">{r.id.slice(0, 8)}{r.created_at ? ` · ${fmtDateTime(r.created_at)}` : ""}</div>
            </div>
            <Button data-testid={`delete-record-${r.id}`} disabled={busy} onClick={() => setDelTarget({ id: r.id, label: r.label })} className="h-8 bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 font-semibold text-xs"><Trash size={14} weight="bold" /></Button>
          </div>
        ))}
      </div>

      {delTarget && (
        <RecordDeleteDialog entity={active} id={delTarget.id} label={delTarget.label} isSuperAdmin={isSuperAdmin}
          onClose={() => setDelTarget(null)} onDeleted={refresh} />
      )}
      {bulkOpen && (
        <BulkDeleteDialog entity={active} ids={selectedIds} protectedRec={activeProtected} isSuperAdmin={isSuperAdmin}
          onClose={() => setBulkOpen(false)} onDone={refresh} />
      )}
      <Dialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <DialogContent data-testid="purge-dialog" className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><Warning size={20} weight="bold" />Purge all TEST data?</DialogTitle>
            <DialogDescription>This permanently removes every TEST-prefixed record in your organisation. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <Input data-testid="purge-confirm-input" value={purgeTxt} onChange={(e) => setPurgeTxt(e.target.value)} placeholder="Type DELETE to confirm" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setPurgeOpen(false)}>Cancel</Button>
            <Button data-testid="purge-confirm-btn" disabled={busy || purgeTxt.trim().toUpperCase() !== "DELETE"} onClick={doPurge} className="bg-red-600 hover:bg-red-700 font-bold">Purge</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
